import React from 'react';

import { currentOrganization } from '@fastlessjs/backoffice-layer/dist/hooks';
import { featureVoter } from '@fastlessjs/backoffice-layer/dist/services';
import { JSONSchemaType } from 'ajv';

import { t } from '../../config/translations';
import { navigateOrder } from '../../orders/schemas/orderList';
import { ProcessingStatusTag } from '../components/ProcessingStatusTag';
import { ReturnStatusTag } from '../components/ReturnStatusTag';
import { Trace, TraceReturn } from '../types';
import { getTraceListItemPath } from '../utils/traceNavigation';

const navigateTraceDetails = (record: Trace) => {
  const org = currentOrganization()?.organizationId?.toLowerCase() || '';
  return `/${org}/${getTraceListItemPath(record)}`;
};

export const traceSchema = (): JSONSchemaType<Trace> => ({
  get title() {
    return t('schema.tracking');
  },
  type: 'object',
  properties: {
    enterpriseId: {
      type: 'string',
      get title() {
        return t('schema.enterpriseId');
      },
    },
    suiviId: {
      type: 'string',
      get title() {
        return t('schema.trackingRef');
      },
      decorators: {
        search: true,
        searchable: true,
        id: { viewAll: true },
        navigate: navigateTraceDetails,
      },
    },
    returnStatus: {
      type: 'string',
      get title() {
        return t('schema.returnStatus');
      },
      nullable: true,
      decorators: {
        search: true,
        action: (_: unknown, record: Trace) => (
          <ReturnStatusTag status={record.displayReturnStatus || record.returnStatus} />
        ),
      },
    },
    processingStatus: {
      type: 'string',
      get title() {
        return t('schema.processingStatus');
      },
      nullable: true,
      decorators: {
        search: true,
        action: (_: unknown, record: Trace) => (
          <ProcessingStatusTag status={record.displayProcessingStatus} />
        ),
      },
    },
    eurekaNsui: {
      type: 'number',
      nullable: true,
      decorators: { hide: ['list', 'details'] },
    },
    eurekaStatus: {
      type: 'string',
      nullable: true,
      decorators: { hide: ['list', 'details'] },
    },
    displayReturnStatus: {
      type: 'string',
      nullable: true,
      decorators: { hide: ['list', 'details'] },
    },
    displayProcessingStatus: {
      type: 'string',
      nullable: true,
      decorators: { hide: ['list', 'details'] },
    },
    nsui: {
      type: 'string',
      nullable: true,
      decorators: { hide: ['list', 'details'] },
    },
    errorMessage: {
      type: 'string',
      nullable: true,
      decorators: { hide: ['list', 'details'] },
    },
    orderId: {
      type: 'string',
      get title() {
        return t('schema.orderRef');
      },
      decorators: {
        search: true,
        searchable: true,
        id: { viewAll: true },
        navigate: featureVoter('menus:orders') ? navigateOrder : undefined,
      },
    },
    _createdAt: {
      type: 'string',
      get title() {
        return t('schema.trackingDate');
      },
      decorators: {
        dateFormat: { stringFormat: 'dd/MM/yyyy HH:mm' },
        search: true,
        searchable: true,
      },
    },
    clientUserName: {
      type: 'string',
      get title() {
        return t('schema.name');
      },
      decorators: { search: true, searchable: true },
    },
    clientUserEmail: {
      type: 'string',
      get title() {
        return t('schema.email');
      },
      decorators: { search: true, searchable: true },
    },
    createdByEmail: {
      type: 'string',
      get title() {
        return t('schema.creatorEmail');
      },
      decorators: {
        search: featureVoter('display:createdByEmail'),
      },
    },
    createdByRole: {
      type: 'string',
      get title() {
        return t('schema.creatorRole');
      },
      decorators: {
        search: featureVoter('display:createdByRole'),
      },
    },
    cal0002: {
      type: 'string',
      get title() {
        return t('schema.trackingUrl');
      },
      decorators: { search: true, link: true },
    },
    // Not shown in the list: kept because JSONSchemaType<Trace> requires them,
    // and cal0006 still feeds the orderId fallback in trace/hooks.
    cal0004: { type: 'string' },
    cal0005: { type: 'string' },
    cal0006: { type: 'string' },
  },
  required: [],
});

export const traceReturnSchema: JSONSchemaType<TraceReturn> = {
  get title() {
    return t('schema.tracking');
  },
  type: 'object',
  properties: {
    enterpriseId: {
      type: 'string',
      get title() {
        return t('schema.enterpriseId');
      },
      decorators: { search: true },
    },
    nsui: {
      type: 'string',
      get title() {
        return t('schema.trackingRef');
      },
      decorators: { search: true, copy: true },
    },
    libe: {
      type: 'string',
      get title() {
        return t('schema.trackingType');
      },
      decorators: { search: true },
    },
    dlit: {
      type: 'string',
      get title() {
        return t('schema.trackingDate');
      },
      decorators: { search: true },
    },
    pali: {
      type: 'string',
      get title() {
        return t('schema.returnMotif');
      },
      decorators: { hide: ['details'] },
    },
    userId: {
      type: 'string',
      get title() {
        return t('schema.userId');
      },
      decorators: { hide: ['details'] },
    },
    createdByEmail: {
      type: 'string',
      get title() {
        return t('schema.createdBy');
      },
      decorators: { copy: true },
    },
    clientUserEmail: {
      type: 'string',
      get title() {
        return t('schema.clientEmail');
      },
      decorators: { search: true },
    },
    clientUserName: {
      type: 'string',
      get title() {
        return t('schema.clientName');
      },
      decorators: { search: true },
    },
    processingStatus: {
      type: 'string',
      get title() {
        return t('schema.processingStatus');
      },
      nullable: true,
      decorators: { search: true },
    },
    ceco: {
      type: 'string',
      nullable: true,
      decorators: { hide: ['list', 'details'] },
    },
    returnStatus: {
      type: 'string',
      nullable: true,
      decorators: { hide: ['list', 'details'] },
    },
    eurekaNsui: {
      type: 'number',
      nullable: true,
      decorators: { hide: ['list', 'details'] },
    },
    eurekaStatus: {
      type: 'string',
      nullable: true,
      decorators: { hide: ['list', 'details'] },
    },
    cal0002: {
      type: 'string',
      get title() {
        return t('schema.trackingUrl');
      },
      decorators: { link: true },
    },
    // Already hidden from the detail view; kept for JSONSchemaType<TraceReturn>.
    cal0004: { type: 'string', decorators: { hide: ['details'] } },
    cal0005: { type: 'string', decorators: { hide: ['details'] } },
    cal0006: {
      type: 'string',
      get title() {
        return t('schema.orderNumber');
      },
      decorators: { search: true, copy: true },
    },
  },
  required: [],
};
